import React, { useCallback, useMemo } from 'react';
import { useHistory } from 'react-router';
import { BaseLayout } from '../../components/Layout/Layout';
import { TextField } from '../../components/TextField/TextField';
import { useLanguage } from '../../locale/useLanguage';
import { Routes } from '../../routes/types';
import { SubmitHandler, useForm } from 'react-hook-form';

import './SignUpView.css';
import { Button } from '../../components/Button/Button';
import { GoogleButton } from '../../components/GoogleButton/GoogleButton';
import { emailRegexp, passwordRegexp } from '../../helpers/regexp';
import { useSignUpMutation } from '../../apis/mlkApi/mlk.api.hooks';
import { parseRes } from '../../apis/utils';
import {
  useGetPrivacyPolicyQuery,
  useGetTermsAndConditionsQuery,
} from '../../apis/cmsApi/cms.hooks.api';
import { Link } from 'react-router-dom';

type SignUpViewProps = {};

enum PrivacyAndTermsChoices {
  Agree = 'agree',
  Disagree = 'disagree',
}

type RegisterForm = {
  email: string;
  password: string;
  confirmPassword: string;
  privacy: PrivacyAndTermsChoices;
  terms: PrivacyAndTermsChoices;
  obligations: boolean;
};

export const SignUpView: React.FC<SignUpViewProps> = () => {
  // const [t] = useLanguage();
  const languageSettings = useLanguage();
  const t = languageSettings[0];
  const locale = languageSettings[2];
  const history = useHistory();

  const [signUpRequest] = useSignUpMutation();
  const { data: termsAndConditions } = useGetTermsAndConditionsQuery({
    locale,
  });
  const { data: privacyPolicy } = useGetPrivacyPolicyQuery({
    locale,
  });

  const {
    register,
    handleSubmit,
    getValues,
    formState: { errors },
  } = useForm<RegisterForm>();

  const onSubmit: SubmitHandler<RegisterForm> = useCallback(
    async (signUpData) => {
      const { email, password } = signUpData;
      signUpRequest({ email, password }).then((res) => {
        const { successRes, errorRes } = parseRes(res);
        if (successRes.data) {
          history.push(Routes.ConfirmEmail, { isFromSignUp: true });
        }

        if (errorRes.error) {
          // TODO: Handle error here
        }
      });
    },
    [history, signUpRequest]
  );

  const submit = useMemo(() => {
    return handleSubmit(onSubmit);
  }, [handleSubmit, onSubmit]);

  const goHome = useCallback(() => {
    if (history.action === 'POP') {
      history.push(Routes.Home);
    } else {
      history.goBack();
    }
  }, [history]);

  const goToSignIn = useCallback(() => {
    history.push(Routes.SignIn);
  }, [history]);

  const privacyValidation = useMemo(() => {
    return {
      ...register('privacy', {
        required: {
          value: true,
          message: t('signUpView.privacyError'),
        },
        pattern: {
          value: /^agree$/,
          message: t('signUpView.privacyError'),
        },
      }),
    };
  }, [register, t]);

  const termsValidation = useMemo(() => {
    return {
      ...register('terms', {
        required: {
          value: true,
          message: t('signUpView.termsError'),
        },
        pattern: {
          value: /^agree$/,
          message: t('signUpView.termsError'),
        },
      }),
    };
  }, [register, t]);

  const obligationsValidation = useMemo(() => {
    return {
      ...register('obligations', {
        required: {
          value: true,
          message: t('signUpView.termsError'),
        },
      }),
    };
  }, [register, t]);

  return (
    <BaseLayout
      title={t('pageTitles.signUp')}
      isLoading={false}
      hasBack
      onBackClick={goHome}
    >
      <div className="sign-up--body">
        <form onSubmit={submit}>
          <TextField
            {...register('email', {
              required: t('signUpView.emailError'),
              pattern: {
                value: emailRegexp,
                message: t('signUpView.emailError'),
              },
            })}
            type="email"
            placeholder={t('signUpView.email')}
            error={errors.email?.message}
          />
          <TextField
            {...register('password', {
              required: t('signUpView.passwordError'),
              pattern: {
                value: passwordRegexp,
                message: t('signUpView.passwordError'),
              },
            })}
            type="password"
            placeholder={t('signUpView.password')}
            error={errors.password?.message}
          />
          <TextField
            {...register('confirmPassword', {
              required: t('signUpView.confirmPasswordError'),
              validate: (val) =>
                getValues('password') === val ||
                t('signUpView.confirmPasswordError'),
            })}
            type="password"
            placeholder={t('signUpView.confirmPassword')}
            error={errors.confirmPassword?.message}
          />
          <div className="sign-up--terms-container">
            <div className="sign-up--info-container">
              <p className="sign-up--term-description">
                {t('signUpView.privacyPolicyFirstDescription')}{' '}
                {privacyPolicy?.href && privacyPolicy?.target ? (
                  <a
                    className="link"
                    target={privacyPolicy?.target}
                    href={privacyPolicy?.href}
                  >
                    {t('signUpView.privacyPolicy')}
                  </a>
                ) : (
                  <Link to={Routes.PrivacyPolicy} className="link">
                    {t('signUpView.privacyPolicy')}
                  </Link>
                )}{' '}
                {t('signUpView.privacyPolicySecondDescription')}
              </p>
              <div className="sign-up--choices-container">
                <label htmlFor="privacy-field-agree">
                  <input
                    {...privacyValidation}
                    type="radio"
                    value={PrivacyAndTermsChoices.Agree}
                    id="privacy-field-agree"
                  />
                  <span className="sign-up--choice-label">
                    {t('signUpView.agree')}
                  </span>
                </label>
                <label htmlFor="privacy-field-disagree">
                  <input
                    {...privacyValidation}
                    type="radio"
                    value={PrivacyAndTermsChoices.Disagree}
                    id="privacy-field-disagree"
                  />
                  <span className="sign-up--choice-label">
                    {t('signUpView.notAgree')}
                  </span>
                </label>
              </div>
              {errors.privacy?.message && (
                <p className="sign-up--error-message">
                  {errors.privacy?.message}
                </p>
              )}
            </div>
            <div className="sign-up--info-container">
              <p className="sign-up--term-description">
                {t('signUpView.termsAndConditionsFirstDescription')}{' '}
                {termsAndConditions?.href && termsAndConditions?.target ? (
                  <a
                    className="link"
                    target={termsAndConditions.target}
                    href={termsAndConditions.href}
                  >
                    {t('signUpView.termsAndConditions')}
                  </a>
                ) : (
                  <Link to={Routes.TermsAndConditions} className="link">
                    {t('signUpView.termsAndConditions')}
                  </Link>
                )}{' '}
                {t('signUpView.termsAndConditionsSecondDescription')}
              </p>
              <div className="sign-up--choices-container">
                <label htmlFor="terms-field-agree">
                  <input
                    {...termsValidation}
                    type="radio"
                    value={PrivacyAndTermsChoices.Agree}
                    id="terms-field-agree"
                  />
                  <span className="sign-up--choice-label">
                    {t('signUpView.agree')}
                  </span>
                </label>
                <label htmlFor="terms-field-disagree">
                  <input
                    {...termsValidation}
                    type="radio"
                    value={PrivacyAndTermsChoices.Disagree}
                    id="terms-field-disagree"
                  />
                  <span className="sign-up--choice-label">
                    {t('signUpView.notAgree')}
                  </span>
                </label>
              </div>
              {errors.terms?.message && (
                <p className="sign-up--error-message">
                  {errors.terms?.message}
                </p>
              )}
            </div>
            <div className="sign-up--info-container">
              <label htmlFor="terms-field-agree">
                <input
                  {...obligationsValidation}
                  type="checkbox"
                  id="terms-field-agree"
                />
                <span className="sign-up--choice-label">
                  {t('signUpView.obligationsAndResponsibilities')}
                </span>
              </label>
              {errors.obligations?.message && (
                <p className="sign-up--error-message">
                  {errors.obligations?.message}
                </p>
              )}
            </div>
          </div>
          <div className="sign-up--buttons-container">
            <Button type="submit" label={t('signUpView.confirm')} isPrimary />
            <div className="sign-up--login-button">
              <Button
                onClick={goToSignIn}
                type="button"
                label={t('signUpView.alreadyHaveAccount')}
              />
            </div>
          </div>
        </form>
        <div className="sign-up--separator-container">
          <div className="sign-up--line" />
          <p className="sign-up--separator-paragraph">
            {t('signUpView.signUpWith')}
          </p>
          <div className="sign-up--line" />
        </div>
        <GoogleButton />
        {/* <FacebookButton />
        <div className="sign-up--google-button">
          <GoogleButton />
        </div> */}
      </div>
    </BaseLayout>
  );
};
